import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { get } from "../../services/api";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import Select from "react-select";
import { Link, useNavigate } from "react-router-dom";
import { customTableStyles } from "../../assets/CustomTableStyles";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";
import ResourceAllocateModal from "./ResourceAllocateModal";

function ResourceAllocation() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const centerId = localStorage.getItem("centerId");
  const departmentId = localStorage.getItem("departmentId");

  const navigate = useNavigate();

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
  } = useForm();

  const [studentsData, setStudentsData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false); // Added loading state
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [departmentsData, setDepartmentsData] = useState([]);
  const [isResourceAllocateModalVisible, setIsResourceAllocateModalVisible] =
    useState(false);

  const departments = [
    {
      key: "IT",
      value: "IT Department",
      id: 266,
      allocationKey: "isITDeviceAllocated",
      iconNonAllocated: "fa-laptop",
      iconAllocated: "fa-check",
    },
    {
      key: "Uniform",
      value: "Uniform",
      id: 287,
      allocationKey: "isUniformAllocated",
      iconNonAllocated: "fa-tshirt",
      iconAllocated: "fa-check",
    },
    {
      key: "Stationery",
      value: "Stationery",
      id: 288,
      allocationKey: "isStationeryAllocated",
      iconNonAllocated: "fa-book",
      iconAllocated: "fa-check",
    },
    {
      key: "Printing",
      value: "Printing",
      id: 289,
      allocationKey: "isCardPriniting",
      iconNonAllocated: "fa-print",
      iconAllocated: "fa-check",
    },
  ];

  const fetchStudentsByCenterId = async () => {
    try {
      const response = await get(
        `Student/GetAllStudentByCenterIdAsync?centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setStudentsData(response?.data);
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  useEffect(() => {
    fetchStudentsByCenterId();
  }, [centerId, reset]);
  const filteredStudentsData = studentsData.filter((student) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      student.firstName.toLowerCase().includes(searchLower) ||
      student.lastName.toLowerCase().includes(searchLower) ||
      student.code.toLowerCase().includes(searchLower)
    );
  });

  const exportToExcel = () => {
    const filteredData = filteredStudentsData.map((row, index) => ({
      "Sr No": index + 1,
      "Roll No": row.code,
      "Full Name": `${row.firstName} ${row.lastName}`,
      Class: row.className,
      "Section Name": row.sectionName,
      "Date of Birth": row.dateOfBirth.split("T")[0],
      Session: row.sessionName,
      Center: row.centerName,
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");
    XLSX.writeFile(workbook, "Students.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();

    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;
    doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Student List";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const bodyData = filteredStudentsData.map((row, index) => [
      index + 1,
      row.code,
      `${row.firstName} ${row.lastName}`,
      row.className,
      row.sectionName,
    ]);

    doc.autoTable({
      head: [["Sr No.", "Roll No.", "Full Name", "Class", "Section Name"]],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("Students.pdf");
  };

  const handlePrintCard = async (studentId) => {
    if (!studentId) {
      alert("Error: No student ID provided");
      return;
    }

    setLoading(true);
    const API_BASE_URL = process.env.REACT_APP_API_URL;

    try {
      const response = await fetch(
        `${API_BASE_URL}Student/GenerateStudentCard?FirstRegistrationId=${studentId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url);

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error("Error opening the student card:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]')
    );
    tooltipTriggerList.forEach((tooltipTriggerEl) => {
      new window.bootstrap.Tooltip(tooltipTriggerEl);
    });
  }, []);

  const baseColumns = [
    {
      name: "ID",
      selector: (row) => row.id,
      sortable: true,
      width: "70px",
      omit: true,
    },
    {
      name: "Sr No.",
      cell: (row, index) => (currentPage - 1) * rowsPerPage + index + 1,
      sortable: false,
      width: "60px",
    },
    {
      name: "Roll No.",
      selector: (row) => row.code,
      sortable: true,
    },
    {
      name: "Full Name",
      selector: (row) => `${row.firstName} ${row.lastName}`,
      cell: (row) => {
        let imageSrc = "";
        if (row.studentImage === "" || row.studentImage === "string") {
          imageSrc = `${process.env.PUBLIC_URL}/image/team/avatar-placeholder.jpg`;
        } else {
          imageSrc = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages/${row.imagePath}`;
        }
        return (
          <div className="d-flex align-items-center gap-2">
            <div className="table-listing-image-preview">
              <img src={imageSrc} alt="Image" />
            </div>
            <div className="">{row.firstName + " " + row.lastName}</div>
          </div>
        );
      },
      sortable: true,
    },
    {
      name: "Class",
      selector: (row) => row.className,
      sortable: true,
    },
    {
      name: "Section Name",
      selector: (row) => row.sectionName,
      sortable: true,
    },
  ];

  const getDepartmentColumns = () =>
    departments.map((dept) => ({
      name: dept.value,
      selector: (row) => row.className,
      cell: (row) => {
        const isAllocated = row[dept.allocationKey];
        const isPrintingDept = dept.key === "Printing";
        return (
          <button
            type="button"
            className={`btn px-2 py-1 border-0 ${
              isAllocated ? "btn-primary" : "btn-outline-primary"
            }`}
            disabled={!isPrintingDept && isAllocated}
            onClick={() => {
              if (isPrintingDept) {
                handlePrintCard(row.id);
              } else if (!isAllocated) {
                handleAllocateButtonClick(row, dept.key);
              }
            }}
            data-bs-toggle="tooltip"
            data-bs-custom-class="custom-tooltip"
            title={isAllocated ? "Allocated" : "Allocate"}
          >
            <i
              className={`fas ${
                isAllocated ? dept.iconAllocated : dept.iconNonAllocated
              }`}
            ></i>
          </button>
        );
      },
    }));

  const columns =
    departmentId === "0"
      ? [...baseColumns, ...getDepartmentColumns()]
      : [
          ...baseColumns,
          ...getDepartmentColumns().filter(
            (col) =>
              col.name ===
              departments.find((d) => d.id.toString() === departmentId)?.value
          ),
        ];

  const handleAllocateButtonClick = (row, department) => {
    setSelectedRow(row);
    setSelectedDepartment(department);
    setIsResourceAllocateModalVisible(true);
  };

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Resource Allocation</h2>
      </div>

      <div className="card">
        <div className="card-body">
          <div
            className="d-flex align-items-center mb-3"
            style={{ marginTop: 0 }}
          >
            <div className="ms-auto" style={{ marginTop: 0 }}>
              <div className="d-flex align-items-center gap-1">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button
                  type="button"
                  data-bs-toggle="tooltip"
                  data-bs-custom-class="custom-tooltip"
                  title="Export to Excel"
                  className="btn btn-outline-success px-2 py-1"
                  onClick={exportToExcel}
                >
                  <i className="fs-8 fa fa-file-excel"></i>
                </button>
                <button
                  type="button"
                  data-bs-toggle="tooltip"
                  data-bs-custom-class="custom-tooltip"
                  title="Export to PDF"
                  className="btn btn-outline-danger px-2 py-1"
                  onClick={exportToPDF}
                >
                  <i className="fs-8 fa fa-file-pdf"></i>
                </button>
              </div>
            </div>
          </div>

          <DataTable
            columns={columns}
            data={filteredStudentsData}
            pagination
            dense
            customStyles={customTableStyles}
            highlightOnHover={true}
            onChangePage={setCurrentPage}
            onChangeRowsPerPage={setRowsPerPage}
          />
        </div>
      </div>

      {isResourceAllocateModalVisible && (
        <ResourceAllocateModal
          rowData={selectedRow}
          department={selectedDepartment}
          onClose={() => setIsResourceAllocateModalVisible(false)}
          refreshOnSubmitSuccess={fetchStudentsByCenterId}
        />
      )}
    </>
  );
}

export default ResourceAllocation;
